import type { TradeType } from '@/types/trades.types';
import { formatDateTimeUtc, formatDateTimeLocal } from '@/utils/format';
import { belowMtaReason, resolveMtaCurrency } from '@/utils/trades/mta-display';

/** The chart range selector window (1M/3M/1Y). */
export type ChartRange = '1M' | '3M' | '1Y';
export const CHART_RANGES: ChartRange[] = ['1M', '3M', '1Y'];

/** Sign palette — single source for the chart, the rail sparkline, and chart accents. The hexes
 *  equal the lr.pos / lr.neg design tokens (SVG fill/stroke take literal colours). */
export const SIGN_COLOR = { pos: '#15803d', neg: '#dc2626', neutral: '#9aa1ad' } as const;
/** MTM exposure accent (= lr.accent token value). */
export const EXPOSURE_ACCENT = '#2563eb';
/** Neutral stroke for CONTENT-BEARING graphics (LR-542 review kvi #2). SIGN_COLOR.neutral
 *  (#9aa1ad) is 2.6:1 on white — the exact hex LR-208 r6 M17 rejected for text, and under the
 *  sparkline's 0.85 opacity it also fails the 3:1 non-text floor (WCAG 1.4.11). #6b7280 is the
 *  lr.muted token: comfortably above 3:1, still reads as neutral. SIGN_COLOR.neutral remains
 *  for MarginChart's zero bars, where the COLOUR carries no information (the ≈0 height and the
 *  tooltip do) — so the 1.4.11 floor doesn't apply there (kvi round-2 #3). */
export const SPARK_NEUTRAL = '#6b7280';

// LR-498: shared presentational helpers for the History master–detail shell. The
// line-item tables (RunLineItems, RepoCashPoolLedger) follow the existing blotter
// palette (gray/green/red) for consistency with the rest of the trade pages; the new
// shell chrome (rail, header, charts) uses the lr.* design tokens.

/** + in our favour (green), − against us (red), 0 neutral — matches MtmRunHistory/MtmAggregateBar. */
export function netClass(value: number): string {
  return value < 0 ? 'text-red-600' : value > 0 ? 'text-green-600' : 'text-gray-900';
}

// LR-577 (feedback #68): the direction WORD behind the sign netClass already colours. `+` = due to
// us — a lend marked up raises the collateral the counterparty owes us (MtmRunMarkSerializer signs
// lend +, borrow −, so the run net carries that convention). Zero has no direction and never gets
// an invented one. Deliberately not activity-family's formatSignedImpact: that one is string-ops
// over decimal strings with its own tone/non-fiat policy, while History works in Numbers.

/** Table/footer label: "Due to us" / "Due to CP"; neutral "Net" at zero. */
export function netDirectionLabel(value: number): string {
  return value > 0 ? 'Due to us' : value < 0 ? 'Due to CP' : 'Net';
}

/** Lower-case phrase for the detail hero suffix; '' at zero so the hero label stays bare. */
export function netDirectionCaption(value: number): string {
  return value > 0 ? 'due to us' : value < 0 ? 'due to CP' : '';
}

// Dates in the History chrome follow the desk convention `YYYY-MM-DD` / `MM-DD`
// (LR-667) — the same shape as every other date in the product, fixed-width and
// free of locale month names. The objects keep the `.format(Date)` call sites of the
// Intl formatters they replace.
//
// LR-679: they read UTC components, not the viewer's local ones. The rest of the
// product is on one timezone (LR-666: blotter legs, trade card, Activity all print
// UTC), and History was the exception: a run marked at 23:30 UTC landed under the
// NEXT day's heading for a viewer east of UTC while the same event read as today in
// the trade card's Activity — two screens, two dates, one event. The grouping keys
// are built from `dayFmt` too, so this fixes which day a run is filed under, not
// just how it prints. Explicit 'en-US' for the time per project convention (never
// rely on the system locale).
const pad2 = (n: number): string => String(n).padStart(2, '0');
export const dayFmt = {
  format: (d: Date): string =>
    `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`,
};
export const dayShortFmt = {
  format: (d: Date): string => `${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`,
};
export const timeFmt = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'UTC',
});

/**
 * Hover text for a History timestamp: the canonical UTC value plus the viewer's
 * own clock, the LR-666 pattern (`formatDateTimeUtc` + "Local time: …"). The rail's
 * time column is 38px wide and cannot carry a "UTC" suffix, so there the tooltip is
 * the only place the zone is spelled out; the wider ledgers print the suffix AND
 * carry this title.
 */
export function timeTitle(d: Date): string {
  const iso = d.toISOString();
  return `${formatDateTimeUtc(iso)}\nLocal time: ${formatDateTimeLocal(iso)}`;
}

/** Signed colour for the shell CHROME (rail rows, detail hero) using the lr design tokens. The
 *  ported line-item tables keep the existing blotter `netClass` (green-600/red-600) instead. */
export function signedToneClass(value: number): string {
  return value < 0 ? 'text-lr-neg' : value > 0 ? 'text-lr-pos' : 'text-lr-fg';
}

/** Signed grouped integer with the design's glyphs (no currency code): "+1,565,000" / "−63,000". */
export function signedNumber(value: number): string {
  const s = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${s}${Math.abs(value).toLocaleString('en-US')}`;
}

// LR-498 v2 (T1-4): one consistent money format across the shell chrome. Raw toLocaleString gives
// mixed decimals (+81,301.5 / +1,207,124.61 / −0.4) that are hard to scan. The rail / list values /
// money chips use the abbreviated `moneyAbbrev`; the detail hero uses the full-precision `moneyFull`.
// Both reuse signedNumber's glyphs (U+2212 minus, unsigned 0). Line-item tables keep the auditable
// blotter formatters (formatCurrencyCode/Exact) with the currency code — these two are code-less,
// the currency is carried by a separate tag in the chrome.

/** Abbreviated, sign-prefixed — scannable, not auditable: "+1.21M" / "+81.3K" / "+8,130" / "−0.4". */
export function moneyAbbrev(value: number): string {
  const s = value > 0 ? '+' : value < 0 ? '−' : '';
  const a = Math.abs(value);
  if (a >= 1e6) return `${s}${(a / 1e6).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (a >= 1e4) return `${s}${(a / 1e3).toFixed(1).replace(/\.0$/, '')}K`;
  if (a >= 1) return `${s}${a.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `${s}${a.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

/** LR-493: label a cross-currency FX pair for the detail chrome, e.g. "GBP→EUR 1.18". The rate is
 *  trimmed to its stored 6dp scale with trailing zeros dropped; omit/blank rate ⇒ the code-only pair
 *  ("GBP→EUR"). Used by the run/margin-call FX chip and the drill-down denomination footnote. */
export function fxPairLabel(
  from: string,
  to: string,
  rate?: string | number | null
): string {
  const pair = `${from}→${to}`;
  if (rate == null || rate === '') return pair;
  const n = Number(rate);
  // A real FX rate is always > 0; treat 0 / whitespace / non-numeric as absent (code-only pair)
  // rather than rendering a nonsensical "GBP→EUR 0".
  return Number.isFinite(n) && n > 0 ? `${pair} ${Number(n.toFixed(6))}` : pair;
}

/** LR-493: the value tag + sub-line prefix for a repo cash-pool book on the "By counterparty" rail.
 *  The running balance (cashpool_after) is DENOMINATED in the settlement currency, so the tag beside
 *  the figure must be the settlement ccy — tagging it with the trade ccy the book is keyed by
 *  mislabels a cross-settle balance (an EUR figure shown as GBP) and contradicts the detail hero. On a
 *  cross-settle book the trade→settle pair prefixes the sub-line so the book stays identifiable; a
 *  same-ccy / legacy book (blank settlement) keeps the trade ccy and no prefix (back-compat). */
export function poolRailTag(
  tradeCcy: string,
  settlementCcy?: string | null
): { currency: string; metaPrefix: string } {
  const settle = settlementCcy || tradeCcy;
  const cross = settle.toUpperCase() !== tradeCcy.toUpperCase();
  return { currency: settle, metaPrefix: cross ? `${tradeCcy}→${settle} · ` : '' };
}

/** Full precision (2dp), sign-prefixed — for the detail hero: "+1,234,567.89" / "−0.40". */
export function moneyFull(value: number): string {
  const s = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${s}${Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Whole-day difference (now − d) measured at UTC midnight. Positive ⇒ d is in the past. Shared by
 *  the day-section label and the rail's relative-day meta (relDayWord).
 *  LR-679: UTC, not local — the heading beside the word is a UTC date, so "Today" has to mean the
 *  current UTC day. Measured in local midnights, a 23:30 UTC run east of UTC read "Today" under a
 *  heading dated tomorrow. UTC days are always 24h, so no DST rounding is needed. */
export function dayDiff(now: Date, d: Date): number {
  const startOf = (x: Date): number =>
    Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate());
  return Math.round((startOf(now) - startOf(d)) / 86400000);
}

/** Day-section label: "Today · 2026-06-24" / "Yesterday · 2026-06-23" / "2026-06-20" (UTC days). */
export function relativeDayLabel(d: Date, now: Date): string {
  const date = dayFmt.format(d);
  const diffDays = dayDiff(now, d);
  if (diffDays === 0) return `Today · ${date}`;
  if (diffDays === 1) return `Yesterday · ${date}`;
  return date;
}

/** The marker shown in the "By" column — the FULL email (not the abbreviated local-part). No
 *  frontend users directory exists to resolve an arbitrary email → display name, so the email is
 *  the full identifier we can show; auto-posts → "auto"; nothing → "—". */
export function byLabel(markedBy: string | null, source: 'automatic' | 'manual'): string {
  if (markedBy) return markedBy;
  return source === 'automatic' ? 'auto' : '—';
}

/** LR-498 v2 (T1-3): below-MTA predicate for the shell chrome. A run/value whose magnitude is under
 *  the counterparty's Minimum Transfer Amount → economically nothing transfers, so the chrome mutes
 *  it (a −0.4 GBP move shouldn't render as an alarming red hero). Mirrors evaluateMtaGate's
 *  rounded-cents compare. Only compares when the MTA currency matches the figure's currency; a
 *  currency mismatch is NOT below (never mutes). */
export function belowMta(value: number, mta?: { mta: number; ccy?: string }, ccy?: string): boolean {
  if (!mta || mta.mta <= 0) return false;
  if (ccy && resolveMtaCurrency(mta.ccy) !== ccy.toUpperCase()) return false;
  return Math.round(Math.abs(value) * 100) < Math.round(mta.mta * 100);
}

/** Hero badge for a below-MTA run. */
export function belowMtaBadge(mta: { mta: number; ccy?: string }) {
  return {
    label: 'below MTA · no transfer',
    tone: 'warn',
    reason: belowMtaReason(mta.mta, mta.ccy),
  } as const;
}

/** Stock-L/B mark trade-type pill colours (lend green / borrow blue). */
export function tradeTypePillClass(t: TradeType): string {
  return t === 'lend'
    ? 'bg-green-50 text-green-700'
    : t === 'borrow'
      ? 'bg-blue-50 text-blue-700'
      : 'bg-gray-100 text-gray-600';
}
